/**
 * "Updated 9:03 AM CDT · 3 sources" in the view header, opening into a
 * per-source breakdown.
 *
 * The summary line reports the *oldest* source, so the header can never look
 * fresher than the slowest thing feeding the page, and the dot takes the worst
 * status across all of them. It reads "Checked" rather than "Updated" when
 * that source last moved on a loader run that had nothing to write, and it
 * carries a date whenever the stamp is not from today -- a bare "5:00 AM"
 * against a stamp from Friday is the one way this line can mislead.
 *
 * Clicking opens the list: every upstream system by name, when it last ran,
 * how often it is supposed to run, and -- for the nightly Talon and EBIS jobs
 * that lag a day behind -- what date the data actually covers.
 *
 * A source can also carry a note from its loader's own run record, which is
 * the difference between "this stopped" and "this ran and had nothing to
 * write". Where there is one it is the most useful line in the row, so it
 * sits directly under the timestamp it explains.
 *
 * A source fed by several senders -- QuickBooks, one company file each --
 * also lists each one with its own time. Hovering the stamp shows that list
 * without a click, which is what the stamp is for on Financial Statements:
 * the line reports the company that is furthest behind, and the hover says
 * which one it is and where every other company stands. The list is in the
 * popover too, for a screen with no hover.
 */

import React from "react";
import { Clock } from "lucide-react";
import { useTz } from "./time";

const STATUS_COLOR = {
  ok: "var(--green)",
  stale: "var(--amber)",
  unknown: "var(--text3)",
};

const STATUS_TEXT = {
  ok: "Up to date",
  stale: "Behind schedule",
  unknown: "Unknown",
};

function StatusDot({ status, size = 8 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: STATUS_COLOR[status] || STATUS_COLOR.unknown,
        flexShrink: 0,
        display: "inline-block",
      }}
    />
  );
}

// "9:03 AM CDT" today, the date as well on any other day. A bare time of day
// is only unambiguous today: a stamp from Friday evening rendered as "5:00 PM"
// reads as this afternoon, which is the one thing this line must never do.
function stampOf(value, tz) {
  return tz.toZonedDateKey(value) === tz.toZonedDateKey(new Date())
    ? tz.formatTime(value, { zoned: true })
    : tz.formatDateTime(value, { zoned: true });
}

/* Each part of a source fed by several, in the order the server sends them:
   its own dot, when it last sent and how long ago. One that has sent nothing
   yet says so rather than being left out. */
function PartsTable({ source }) {
  const tz = useTz();
  const cell = { padding: "3px 0", whiteSpace: "nowrap", verticalAlign: "middle" };
  return (
    <table
      aria-label={`${source.label} by ${source.part_label || "part"}`}
      style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}
    >
      <tbody>
        {source.parts.map((part) => (
          <tr key={part.name}>
            <th scope="row" style={{ ...cell, textAlign: "left", fontWeight: 700, paddingRight: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                <StatusDot status={part.status} />
                {part.name}
              </span>
            </th>
            {part.last_updated ? (
              <>
                <td style={{ ...cell, paddingRight: 12 }}>{stampOf(part.last_updated, tz)}</td>
                <td
                  style={{
                    ...cell,
                    textAlign: "right",
                    color: part.status === "stale" ? "var(--amber)" : "var(--text3)",
                  }}
                >
                  {tz.formatRelative(part.last_updated)}
                </td>
              </>
            ) : (
              <td colSpan={2} style={{ ...cell, color: "var(--text3)" }}>
                Nothing received yet
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SourceRow({ source }) {
  const { formatDateTime, formatRelative, formatDate } = useTz();

  const when = source.last_updated
    ? `${formatDateTime(source.last_updated, { zoned: true })} — ${formatRelative(source.last_updated)}`
    : "No successful run recorded";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "0 10px",
        alignItems: "start",
        padding: "9px 0",
        borderTop: "1px solid var(--border)",
      }}
    >
      <span style={{ paddingTop: 6 }}>
        <StatusDot status={source.status} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0 8px", alignItems: "baseline" }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{source.label}</span>
          {source.detail && (
            <span style={{ fontSize: 12, color: "var(--text2)" }}>{source.detail}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>{when}</div>
        {source.parts?.length > 0 && (
          <div style={{ margin: "6px 0 4px" }}>
            <PartsTable source={source} />
          </div>
        )}
        {source.note && (
          <div
            style={{
              fontSize: 12,
              color: source.status === "ok" ? "var(--text2)" : "var(--amber)",
              marginTop: 2,
            }}
          >
            {source.note}
            {source.loader_ran_at && (
              <>
                {" "}
                <span
                  style={{ color: "var(--text3)" }}
                  title={formatDateTime(source.loader_ran_at, { zoned: true })}
                >
                  Last succeeded {formatRelative(source.loader_ran_at)}.
                </span>
              </>
            )}
          </div>
        )}
        {source.data_through && (
          <div style={{ fontSize: 12, color: "var(--text2)" }}>
            Data through {formatDate(source.data_through)}
          </div>
        )}
        {source.cadence_label && (
          <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 2 }}>
            {source.cadence_label}
          </div>
        )}
      </div>
    </div>
  );
}

// How long the pointer rests on the stamp before the list appears, so one
// passing over the header on its way somewhere else does not flash it.
const PEEK_DELAY_MS = 150;

export default function DataFreshness({ freshness }) {
  const tz = useTz();
  const { formatRelative } = tz;
  const [open, setOpen] = React.useState(false);
  const [peek, setPeek] = React.useState(false);
  const wrapRef = React.useRef(null);
  const peekTimer = React.useRef(null);
  const peekId = React.useId();

  const sources = freshness?.sources || [];
  const withParts = sources.filter((source) => source.parts?.length > 0);

  const showPeek = () => {
    if (!withParts.length) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeek(true), PEEK_DELAY_MS);
  };
  const hidePeek = () => {
    clearTimeout(peekTimer.current);
    setPeek(false);
  };
  React.useEffect(() => () => clearTimeout(peekTimer.current), []);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close the popover when the page changes underneath it.
  React.useEffect(() => {
    setOpen(false);
    setPeek(false);
  }, [freshness?.page]);

  if (!sources.length) return null;

  const oldest = freshness.oldest_as_of || freshness.oldest_updated;
  const status = freshness.status || "unknown";

  const stamp = oldest ? stampOf(oldest, tz) : null;

  // "Checked" where the source this line reports -- the slowest one -- last
  // moved because its loader ran and found nothing to load. The data is
  // current as of that run, but nothing was written, and "Updated" would
  // claim more than we know.
  const verb = freshness.oldest_as_of_kind === "checked" ? "Checked" : "Updated";

  const summary = stamp ? `${verb} ${stamp}` : "Update time unavailable";

  const countLabel =
    sources.length === 1
      ? sources[0].label
      : `${sources.length} sources`;

  return (
    <span className="fox-view-freshness" ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => {
          hidePeek();
          setOpen((v) => !v);
        }}
        onMouseEnter={showPeek}
        onMouseLeave={hidePeek}
        onFocus={showPeek}
        onBlur={hidePeek}
        onKeyDown={(e) => {
          if (e.key === "Escape") hidePeek();
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-describedby={peek && !open ? peekId : undefined}
        // Where there is a list to show on hover, the browser's own tooltip
        // would only cover it up.
        title={
          withParts.length
            ? undefined
            : oldest
              ? `${STATUS_TEXT[status]} — ${formatRelative(oldest)}`
              : STATUS_TEXT[status]
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: 0,
          padding: "2px 4px",
          margin: 0,
          borderRadius: 4,
          cursor: "pointer",
          font: "inherit",
          fontSize: "0.75rem",
          color: "var(--muted)",
          whiteSpace: "nowrap",
        }}
      >
        <StatusDot status={status} />
        <span>{summary}</span>
        <span style={{ color: "var(--text3)" }}>· {countLabel}</span>
      </button>

      {peek && !open && (
        <div
          role="tooltip"
          id={peekId}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 60,
            minWidth: 330,
            maxWidth: "min(380px, calc(100vw - 32px))",
            background: "var(--bg2)",
            border: "1px solid var(--border2)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "0 12px 32px -12px rgba(0,0,0,0.28)",
            padding: "10px 14px 10px",
            textAlign: "left",
            whiteSpace: "normal",
            pointerEvents: "none",
          }}
        >
          {withParts.map((source) => (
            <div key={source.key}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--text3)",
                  paddingBottom: 6,
                }}
              >
                <Clock size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Last {source.label} update, by {source.part_label || "part"}
                </span>
              </div>
              <PartsTable source={source} />
            </div>
          ))}
        </div>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Data sources"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 60,
            minWidth: 320,
            maxWidth: "min(420px, calc(100vw - 32px))",
            background: "var(--bg2)",
            border: "1px solid var(--border2)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "0 12px 32px -12px rgba(0,0,0,0.28)",
            padding: "12px 16px 14px",
            textAlign: "left",
            whiteSpace: "normal",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--text3)",
              paddingBottom: 8,
            }}
          >
            <Clock size={13} strokeWidth={2} />
            Data sources
          </div>

          {sources.map((source) => (
            <SourceRow key={source.key} source={source} />
          ))}

          <div
            style={{
              marginTop: 10,
              paddingTop: 9,
              borderTop: "1px solid var(--border)",
              fontSize: 11.5,
              color: "var(--text3)",
              lineHeight: 1.5,
            }}
          >
            Collected in UTC, shown in your timezone. Change it under your name
            in the top right.
          </div>
        </div>
      )}
    </span>
  );
}
